import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Truck,
  HandCoins,
  //UserCog,
  Wallet,
  FileText,
  UserX,
  PhoneOff,
  Bell,
  Radio,
  Settings,
  Smartphone,
  ClipboardList,
  FileCheck,
  ChevronLeft,
  Shield,
  CircleStar,
  Map,
  Cctv,
  Image
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Modern Sidebar Component
 * Professional collapsible navigation with smooth animations
 */

const navigationItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'view_dashboard' },
  { path: '/orders', label: 'Orders', icon: ShoppingCart, permission: 'view_orders' },
  { path: '/customers', label: 'Customers', icon: Users, permission: 'view_customers' },
  { path: '/drivers', label: 'Drivers', icon: Truck, permission: 'view_drivers' },
  { path: '/admins', label: 'Admins', icon: Shield, permission: 'manage_admins' },
  { path: '/agents', label: 'Top-up Agents', icon: HandCoins, permission: 'view_agents' },
  { path: '/blocked-numbers', label: 'Blocked Numbers', icon: PhoneOff, permission: 'manage_blocked_numbers' },
  { path: '/transactions', label: 'Transactions', icon: Wallet, permission: 'view_transactions' },
  { path: '/ratings', label: 'Ratings', icon: CircleStar, permission: 'view_ratings' },
  { path: '/delete-requests', label: 'Delete Requests', icon: UserX, permission: 'view_delete_requests' },
  { path: '/map', label: 'Map', icon: Map, permission: 'view_map' },
  { path: '/notifications', label: 'Notifications', icon: Bell, permission: 'send_notifications' },
  { path: '/broadcasts', label: 'Driver Broadcasts', icon: Radio, permission: 'manage_broadcasts' },
  { path: '/banners', label: 'Banners', icon: Image, permission: 'manage_banners' },
  { path: '/settings', label: 'Settings', icon: Settings, permission: 'manage_settings' },
  { path: '/app-versions', label: 'App Versions', icon: Smartphone, permission: 'manage_app_versions' },
  { path: '/admin-activity', label: 'Admin Activity', icon: ClipboardList, permission: 'view_admin_activity' },
  { path: '/reports', label: 'Reports', icon: FileText, permission: 'view_reports' },
  { path: '/security-logs', label: 'Security Logs', icon: Cctv, permission: 'view_security_logs' },

];

export default function Sidebar({ isOpen, onClose, isMobile, onCollapseChange }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pendingDeleteCount, setPendingDeleteCount] = useState(0);
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // Live count of pending delete requests — shown as a red badge next to the
  // "Delete Requests" item so admins notice new requests immediately.
  useEffect(() => {
    if (!hasPermission('view_delete_requests')) return;
    const q = query(
      collection(db, 'deleteRequests'),
      where('status', '==', 'pending'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setPendingDeleteCount(snap.size),
      (err) => console.error('deleteRequests badge listener error:', err),
    );
    return unsub;
  }, [hasPermission]);

  const toggleCollapse = () => {
    if (!isMobile) {
      const newCollapsedState = !isCollapsed;
      setIsCollapsed(newCollapsedState);
      onCollapseChange?.(newCollapsedState);
    }
  };

  // Filter navigation items based on permissions
  const visibleNavItems = navigationItems.filter(item => hasPermission(item.permission));

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed bg-white/80 backdrop-blur-xl border-r border-slate-200/60 z-50
          transition-all duration-300 ease-in-out shadow-soft-lg
          ${isMobile 
            ? `top-0 left-0 h-screen w-64 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}` 
            : `top-16 left-0 bottom-0 ${isCollapsed ? 'w-20' : 'w-64'}`
          }
        `}
      >
        <div className="flex flex-col h-full">
          {/* Mobile header with logo */}
          {isMobile && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-500 rounded-xl flex items-center justify-center shadow-soft">
                  <span className="text-white font-bold text-lg">D</span>
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Dalverha</h1>
                  <p className="text-xs text-slate-500">Admin Panel</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-1">
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const badgeCount =
                  item.path === '/delete-requests' ? pendingDeleteCount : 0;
                const showBadge = badgeCount > 0;
                const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={isMobile ? onClose : undefined}
                      className={`
                        group flex items-center gap-3 px-3 py-2.5 rounded-xl
                        transition-all duration-200
                        ${isActive
                          ? 'bg-gradient-to-r from-primary-100 to-primary-50 text-primary-700 shadow-soft border border-primary-200'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }
                        ${isCollapsed && !isMobile ? 'justify-center' : ''}
                      `}
                      title={
                        isCollapsed && !isMobile
                          ? `${item.label}${showBadge ? ` (${badgeLabel})` : ''}`
                          : undefined
                      }
                    >
                      <div className="relative flex-shrink-0">
                        <Icon
                          className={`
                            w-5 h-5 transition-colors
                            ${isActive ? 'text-primary-600' : 'text-slate-400 group-hover:text-slate-600'}
                          `}
                        />
                        {/* Collapsed-mode badge: small red dot in the icon corner */}
                        {showBadge && isCollapsed && !isMobile && (
                          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow">
                            {badgeLabel}
                          </span>
                        )}
                      </div>
                      {(!isCollapsed || isMobile) && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                      {/* Expanded-mode badge: pushed to the right of the row */}
                      {showBadge && (!isCollapsed || isMobile) && (
                        <span className="ml-auto min-w-[22px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold leading-none shadow">
                          {badgeLabel}
                        </span>
                      )}
                      {isActive && !showBadge && (!isCollapsed || isMobile) && (
                        <div className="ml-auto w-1.5 h-1.5 bg-primary-600 rounded-full animate-pulse" />
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Collapse button (desktop only) */}
          {!isMobile && (
            <div className="p-3 border-t border-slate-100">
              <button
                onClick={toggleCollapse}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-all duration-200 group"
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <ChevronLeft 
                  className={`
                    w-5 h-5 text-slate-400 group-hover:text-slate-600
                    transition-transform duration-300
                    ${isCollapsed ? 'rotate-180' : ''}
                  `}
                />
                {!isCollapsed && (
                  <span className="text-sm font-medium text-slate-600">Collapse</span>
                )}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
